import { useLayoutEffect } from 'react';

// Smoothly slides draggables to their new position whenever the layout inside `ref` changes
// (placeholder shown/hidden, dragged item hidden, list reordered).
// FLIP technique: remember each item's position, and after a DOM change animate it from the old spot to the new one.
export function useFlipAnimation(ref, duration = 200) {
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root || !duration || typeof MutationObserver === 'undefined') {return;}
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {return;}

    // The offsetLeft/Top values ignore transforms and scrolling, so running animations don't skew the measurement
    const measure = () => {
      const positions = new Map();
      root.querySelectorAll('[data-draggable-id]').forEach((el) => {
        if (el.offsetParent === null) {return;} // Hidden (the item being dragged)
        positions.set(el, { x: el.offsetLeft, y: el.offsetTop });
      });
      return positions;
    };

    let positions = measure();

    const observer = new MutationObserver(() => {
      const next = measure();
      next.forEach((pos, el) => {
        const prev = positions.get(el);
        if (!prev || !el.animate) {return;}

        let dx = prev.x - pos.x;
        let dy = prev.y - pos.y;

        // If it is still moving from a previous change, continue from where it currently is
        if (el.flipAnimation && el.flipAnimation.playState === 'running') {
          const matrix = new DOMMatrix(getComputedStyle(el).transform);
          dx += matrix.m41;
          dy += matrix.m42;
          el.flipAnimation.cancel();
        }
        if (!dx && !dy) {return;}

        el.flipAnimation = el.animate(
          [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0, 0)' }],
          { duration, easing: 'cubic-bezier(0.2, 0, 0, 1)' }
        );
      });
      positions = next;
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'data-dragging']
    });
    return () => observer.disconnect();
  }, [ref, duration]);
}
